import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, LogOut, Send, BadgeCheck, Users, FolderDown, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { fmt } from "@/components/CartDrawer";
import { Reveal } from "@/components/motion";
import { DEALER_TESTIMONIALS } from "@/data/testimonials";

const inputCls = "w-full border border-girder bg-steel px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500";

function DealerLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      if (user.profile_completed === false) {
        toast.message("Almost there", { description: "Confirm your details to finish setting up your account." });
        navigate("/dealer/profile");
        return;
      }
      toast.success(`Welcome back, ${user.name || user.company || "dealer"}`, { description: "Wholesale pricing is now unlocked across the site." });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="clip-corner border border-girder bg-obsidian p-6 sm:p-8" data-testid="dealer-login-form">
      <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-hivis"><Lock className="h-3.5 w-3.5" /> Approved Dealer Login</p>
      <div className="mt-5 space-y-4">
        <div><label className={labelCls} htmlFor="dl-email">Email</label><input id="dl-email" name="email" autoComplete="username" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="dealer-login-email" placeholder="you@signshop.com" /></div>
        <div><label className={labelCls} htmlFor="dl-pass">Password</label><input id="dl-pass" name="password" autoComplete="current-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="dealer-login-password" placeholder="••••••••" /></div>
      </div>
      <button type="submit" disabled={busy} data-testid="dealer-login-submit" className="clip-corner mt-6 w-full bg-hivis py-3.5 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover disabled:opacity-60">
        {busy ? "Unlocking..." : "Unlock Wholesale Pricing"}
      </button>
      <p className="mt-4 text-center">
        <Link to="/dealer-setup" data-testid="dealer-setup-link" className="font-mono text-[11px] uppercase tracking-widest text-slate-500 underline decoration-girder underline-offset-4 transition-colors hover:text-hivis">
          First time or forgot password? Set up your password
        </Link>
      </p>
    </form>
  );
}

function DealerApply() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", business_type: "Sign shop", website: "", address: "", message: "", hp: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/dealer/apply", form);
      toast.success("Application received", { description: "We review dealer applications within one business day." });
      setForm({ name: "", company: "", email: "", phone: "", business_type: "Sign shop", website: "", address: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="clip-corner border border-girder bg-obsidian p-6 sm:p-8" data-testid="dealer-apply-form">
      {/* Honeypot: invisible to humans; bots that fill it are silently dropped server-side */}
      <input type="text" name="hp" value={form.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} data-testid="hp-field" />
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-hivis">Become a Dealer</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div><label className={labelCls} htmlFor="da-name">Contact name *</label><input id="da-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="da-name" /></div>
        <div><label className={labelCls} htmlFor="da-company">Company *</label><input id="da-company" required value={form.company} onChange={set("company")} className={inputCls} data-testid="da-company" /></div>
        <div><label className={labelCls} htmlFor="da-email">Email *</label><input id="da-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="da-email" /></div>
        <div><label className={labelCls} htmlFor="da-phone">Phone *</label><input id="da-phone" required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="da-phone" /></div>
        <div>
          <label className={labelCls} htmlFor="da-type">Business type</label>
          <select id="da-type" value={form.business_type} onChange={set("business_type")} className={inputCls} data-testid="da-type">
            <option>Sign shop</option><option>Wide format printer</option><option>Outdoor media installer</option><option>Other</option>
          </select>
        </div>
        <div><label className={labelCls} htmlFor="da-website">Website</label><input id="da-website" value={form.website} onChange={set("website")} className={inputCls} data-testid="da-website" placeholder="https://" /></div>
      </div>
      <div className="mt-4"><label className={labelCls} htmlFor="da-address">Store address <span className="normal-case text-slate-500">(optional — puts you on the Dealer Locator map)</span></label><input id="da-address" value={form.address} onChange={set("address")} className={inputCls} data-testid="da-address" placeholder="Street, city, state ZIP" /></div>
      <div className="mt-4"><label className={labelCls} htmlFor="da-message">Tell us about your business</label><textarea id="da-message" rows={3} value={form.message} onChange={set("message")} className={inputCls} data-testid="da-message" /></div>
      <button type="submit" disabled={busy} data-testid="dealer-apply-submit" className="clip-corner mt-6 flex w-full items-center justify-center gap-3 bg-hivis py-3.5 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover disabled:opacity-60">
        {busy ? "Submitting..." : "Submit Application"} <Send className="h-4 w-4" />
      </button>
    </form>
  );
}

function DealerDashboard() {
  const { dealer, logout } = useAuth();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="clip-corner border border-hivis/40 bg-obsidian p-6 sm:p-8" data-testid="dealer-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-hivis"><BadgeCheck className="h-4 w-4" /> Wholesale Unlocked</p>
          <h3 className="mt-2 font-display text-2xl font-bold uppercase text-slate-100">{dealer.company || dealer.name}</h3>
        </div>
        <button onClick={logout} data-testid="dealer-logout" className="flex items-center gap-2 border border-girder px-4 py-2 font-mono text-xs uppercase tracking-widest text-slate-400 transition-colors hover:border-hivis hover:text-hivis">
          <LogOut className="h-3.5 w-3.5" /> Log out
        </button>
      </div>
      <p className="mt-4 text-sm text-slate-400">Your dealer pricing is now visible on every product page and below. Order through the cart or call 970-650-5173 for net terms.</p>
      <Link to="/dealer-assets" data-testid="dealer-assets-link" className="clip-corner-sm mt-5 flex items-center justify-between border border-girder bg-steel/40 px-5 py-3.5 transition-colors hover:border-hivis">
        <span className="flex items-center gap-3"><FolderDown className="h-5 w-5 text-hivis" /><span className="font-display text-lg font-bold uppercase text-slate-100">Dealer Asset Library</span></span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Logos & artwork</span>
      </Link>
      <Link to="/dealer/profile" data-testid="dealer-profile-link" className="clip-corner-sm mt-4 flex items-center justify-between border border-girder bg-steel/40 px-5 py-3.5 transition-colors hover:border-hivis">
        <span className="flex items-center gap-3"><ClipboardCheck className="h-5 w-5 text-hivis" /><span className="font-display text-lg font-bold uppercase text-slate-100">Update Your Details</span></span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Contact & shipping</span>
      </Link>
      {dealer.is_admin && (
        <Link to="/admin/dealers" data-testid="admin-tracker-link" className="clip-corner-sm mt-5 flex items-center justify-between border border-hivis/40 bg-steel/40 px-5 py-3.5 transition-colors hover:border-hivis">
          <span className="flex items-center gap-3"><Users className="h-5 w-5 text-hivis" /><span className="font-display text-lg font-bold uppercase text-slate-100">Dealer Activation Tracker</span></span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Owner tools</span>
        </Link>
      )}
      <div className="mt-6 divide-y divide-girder border border-girder" data-testid="wholesale-price-list">
        {products.map((p) => (
          <div key={p.slug} className="flex items-center justify-between gap-4 px-5 py-3">
            <span className="font-display text-lg font-bold uppercase text-slate-200">{p.name}</span>
            <span className="flex items-center gap-4 font-mono text-sm">
              <span className="text-slate-500 line-through">{fmt(p.price)}</span>
              <span className="text-hivis" data-testid={`dealer-price-${p.slug}`}>{p.wholesale_price ? fmt(p.wholesale_price) : "—"}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DealerPortalPage() {
  const { dealer, checking } = useAuth();
  return (
    <div data-testid="dealer-portal-page">
      <PageHeader
        kicker="Wholesale Portal"
        title="Dealer pricing for the sign industry"
        sub="Sign shops, wide format printers and installers buy The Banner Bar at wholesale. Apply once — approved dealers see dealer rates everywhere on the site."
        image="/assets/img/w-vconfig-bg-sdi0655.webp"
      />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24">
        {checking ? (
          <p className="font-mono text-sm uppercase tracking-widest text-slate-500">Checking session...</p>
        ) : dealer && dealer.profile_completed === false ? (
          <Reveal>
            <div className="clip-corner mx-auto max-w-xl border border-hivis/40 bg-obsidian p-8 text-center" data-testid="profile-incomplete-card">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-hivis">Almost There</p>
              <h3 className="mt-3 font-display text-2xl font-bold uppercase text-slate-100">Confirm your details</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">One quick step before wholesale pricing unlocks — confirm your contact and shipping info so orders and dealer assets reach the right place.</p>
              <Link to="/dealer/profile" data-testid="complete-profile-cta" className="clip-corner mt-6 inline-flex items-center gap-2 bg-hivis px-8 py-3.5 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">
                Confirm your details
              </Link>
            </div>
          </Reveal>
        ) : dealer ? (
          <Reveal><DealerDashboard /></Reveal>
        ) : (
          <>
            <div className="grid gap-8 lg:grid-cols-2">
              <Reveal><DealerLogin /></Reveal>
              <Reveal i={1}><DealerApply /></Reveal>
            </div>
            <div className="mt-12" data-testid="dealer-quotes">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-hivis">What dealers say</p>
              <div className="mt-4 grid gap-5 md:grid-cols-2">
                {DEALER_TESTIMONIALS.slice(0, 2).map((t) => (
                  <figure key={t.name} className="clip-corner border border-girder bg-steel/40 p-6">
                    <blockquote className="text-sm leading-relaxed text-slate-300">"{t.quote}"</blockquote>
                    <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500">{t.name} · {t.role}, {t.company}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
